import {alias, define, inject, injectAlias, singleton} from '@injex/core';
import {Injex} from '@injex/webpack';
import {makeObservable, observable} from 'mobx';
import IDisposable from '../../../common/interfaces/IDisposable';
import DataQuery from '../../../common/models/DataQuery';
import {isDefined, Arrays} from '@vidazoo/ui-kit';
import {AuthRole, AuthStatus} from '../common/enums';
import {ISession, IUser} from '../interfaces/IAccountsSDK';
import IViewAsUser from '../interfaces/IViewAsUser';
import {IUserMetadata} from '../interfaces/IUserMetadata';
import ApiManager from '../../../common/api/ApiManager.mdl';
import UserManager from "../../../common/managers/UserManager.mdl";
import Hook from '../../../common/utils/Hook';
import {RequestContext} from '../../../common/utils/EntityRequestAdapter';

export type SessionHooks = {
    loginSuccess: Hook;
    statusChange: Hook<[status: AuthStatus]>;
    metadataLoad: Hook<[metadata: IUserMetadata, session: ISession]>;
    initCompleted: Hook<[user: IUser]>
};

export type TokenProvider = () => Promise<string>;

export const VIEW_AS_ROLES = [AuthRole.VIEW_AS, AuthRole.VIEW_AS_STRICT];
export const ADMIN_ROLES = [AuthRole.ADMIN, AuthRole.ROOT_ADMIN, AuthRole.SUPER_ADMIN];
export const CURRENT_SCOPE = "bi_dashboards";
export const VIEW_AS_STORAGE_KEY = "viewAs";

const OPTIMISTIC_LOAD = process.env.NODE_ENV === 'development';


@define()
@singleton()
@alias('Disposable')
export class SessionManager implements IDisposable {
    @inject() private $injex: Injex;
    @inject() private apiManager: ApiManager;
    @inject() private userManager: UserManager;

    @injectAlias('Disposable') private disposables: IDisposable[];

    public hooks: SessionHooks;
    public canViewAs: boolean;
    public hasAdminRole: boolean;
    public userLoggedOut: boolean;
    public userMetadata: DataQuery<IUserMetadata>;

    /** Wired from the Auth0 gate to log the user out through the Auth0 SPA client. */
    public authLogout?: () => void;

    @observable public status: AuthStatus;
    @observable public initialAuthorization: boolean;
    @observable public viewAsUsers: DataQuery<IViewAsUser[]>;

    private _lastSeen: number;
    private _accessToken: string;
    private _tokenProvider: TokenProvider;
    private _auth0User: any;
    private _authenticated: boolean;
    private _selectedAccounts: string[];
    private _session: ISession;

    constructor() {
        makeObservable(this);

        this.status = AuthStatus.Authorizing;
        this.initialAuthorization = true;
        this.canViewAs = false;
        this.hasAdminRole = false;
        this.userMetadata = new DataQuery<IUserMetadata>(this._fetchUserMetadata.bind(this));
        this.userLoggedOut = false;
        this._lastSeen = 0;
        this._authenticated = false;
        this._accessToken = null;
        this._selectedAccounts = [];
        this._onVisibilityChange = this._onVisibilityChange.bind(this);

        this.hooks = {
            loginSuccess: new Hook(),
            statusChange: new Hook(),
            metadataLoad: new Hook(),
            initCompleted: new Hook()
        };
    }

    /**
     * No-op kept for backward compatibility. The Auth0 SPA client is now
     * initialized by the Auth0Provider and wired into the SessionManager
     * through the Auth0 gate (see modules/session/components/auth-gate).
     */
    public initialize() {
        // Auth0Provider handles the authentication lifecycle.
    }

    /**
     * Wires the Auth0 `getAccessTokenSilently` provider so the API/socket
     * layers can retrieve a fresh access token on demand.
     */
    public setTokenProvider(provider: TokenProvider) {
        this._tokenProvider = provider;
    }

    /**
     * Called by the Auth0 gate once the user is authenticated. Bootstraps the
     * backend session from `/api/accounts/me`, mirroring the previous
     * `_onSessionChanged` SDK flow.
     */
    public async onSessionChange(token: string, auth0User: any) {
        this._accessToken = token;
        this._auth0User = auth0User;
        this._authenticated = true;
        this.initialAuthorization = false;

        try {
            OPTIMISTIC_LOAD
                ? this.hooks.loginSuccess.call()
                : await this.hooks.loginSuccess.call();

            await this.userMetadata.fetch();

            const metadata = this.userMetadata.data;

            this._session = {user: metadata.auth} as unknown as ISession;
            this._selectedAccounts = Array.isArray(metadata.accounts)
                ? metadata.accounts.map((account: any) => account && account._id).filter(Boolean)
                : [];

            this.hooks.metadataLoad.call(metadata, this._session);

            this.canViewAs = true; //TODO :check
            this.hasAdminRole = true; //TODO :check

            this._setStatus(AuthStatus.LoggedIn);

            this._lastSeen = Date.now();

            document.addEventListener('visibilitychange', this._onVisibilityChange);

            this._initCompleted(this.user);
        } catch (error) {
            this.$injex.logger.error('[SESSION INIT FAILED]', error);
            this._authenticated = false;
            this._setStatus(AuthStatus.LoggedOut);
            this.disposables.forEach((disposable) => disposable.dispose());
        }
    }

    /**
     * Returns a fresh access token from Auth0. Falls back to the cached token
     * if the provider is unavailable or throws.
     */
    public async getAccessToken(): Promise<string> {
        if (this._tokenProvider) {
            try {
                const token = await this._tokenProvider();
                if (token) {
                    this._accessToken = token;
                }
            } catch (error) {
                this.$injex.logger.error('[GET ACCESS TOKEN FAILED]', error);
            }
        }

        return this._accessToken;
    }

    public getSelectedAccounts(): string[] {
        return this._selectedAccounts || [];
    }

    public getRequestContext(): RequestContext {
        if (this.isAuthenticated) {
            return {
                accessToken: this._accessToken,
                accounts: this.getSelectedAccounts()
            };
        }

        return {};
    }

    public get isAuthenticated(): boolean {
        return this._authenticated;
    }

    public get hasAppAccess(): boolean {
        return this.isUserAllowed("read:data");
    }

    public isUserAllowed = (requiredScope: string) => {
        return this.session?.user?.scopes?.some((scope) => scope === requiredScope);
    }

    public get session() {
        if (!this.isAuthenticated) {
            return null;
        }

        return this._session;
    }

    public get auth0User() {
        return this._auth0User;
    }

    public get user() {
        if (!this.isAuthenticated || !this.session) {
            return null;
        }

        return this.session.user;
    }

    public get fullName(): string {
        if (!this.isAuthenticated) {
            return '';
        }

        return `${this.user.firstName} ${this.user.lastName}`;
    }

    public get email(): string {
        if (!this.isAuthenticated) {
            return '';
        }

        return this.user.email;
    }

    public get org(): string {
        if (!this.isAuthenticated) {
            return '';
        }

        return this.user.organization?.name || '';
    }

    public logout() {
        this.userLoggedOut = true;
        this._authenticated = false;

        if (this.authLogout) {
            this.authLogout();
        }
    }
    /**
     * Checks for session validity when user returns to the
     * Tab/Browser after idle time.
     *
     * If the user returns after 1 minute, we refresh the Auth0
     * access token. If the refresh fails, we sign the user out.
     */
    private async _onVisibilityChange() {
        if (!this.isAuthenticated) {
            return;
        }

        const now = Date.now();

        if (document.visibilityState === 'visible') {
            if (this._lastSeen + 60_000 < now) {
                this.$injex.logger.debug('[PING SESSION]');
                try {
                    await this.getAccessToken();
                } catch {
                    this.logout();
                }
            }
        } else {
            this._lastSeen = now;
        }
    }

    private _setStatus(status: AuthStatus) {
        if (this.status === status) {
            return;
        }

        this.status = status;
        this.hooks.statusChange.call(this.status);
    }

    public dispose() {
        this.initialAuthorization = true;
        this.canViewAs = false;
        this.hasAdminRole = false;
        this.viewAsUsers && this.viewAsUsers.reset();
        this.userMetadata.reset();
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }

    private async _fetchUserMetadata(): Promise<IUserMetadata> {
        const userMetadata = await this.apiManager.accounts.me();

        this.$injex.logger.debug('[USER METADATA]', userMetadata);

        return userMetadata;
    }

    private _initCompleted(user: IUser) {
        this.hooks.initCompleted.call(user);
    }
}
